import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import { usePhoneInputStyles } from './phoneInput.styles';
import { useEffect, useRef, useState, type JSX } from 'react';
import { availableCountries, availableCountriesMap } from './constants';
import { v4 as uuidV4 } from 'uuid';
import Box from '@mui/material/Box';
import { Status } from '../../status';

const DEFAULT_COUNTRY = 'us';

/**
 * Using the same component as in OG to avoid breaking changes and allow the same capabilities as we had there.
 * It is more agile and allow you to enter the phone number in different standards.
 */
export type PhoneInputProps = {
  id?: string;
  name?: string;
  defaultCountry: string | undefined;
  hasError?: boolean;
  isDisabled?: boolean;
  /**
   * FullNumber
   */
  value?: string;
  autoFocus?: boolean;
  previouslySelected?: string[];
  placeholder?: string;
  /**
   * Returns ref to the IntlTelInput input element.
   */
  onInputElement?: (ref: HTMLInputElement | null) => void;
  onChange: (value: string) => void;
  onCountryIsoChanged?: (iso: string) => void;
  onBlur?: () => void;
  onUpdatePreviouslySelected?: (value: string) => void;
};

export const PhoneInput = ({
  id,
  name,
  defaultCountry = DEFAULT_COUNTRY,
  hasError,
  isDisabled,
  value: fullNumber,
  autoFocus,
  previouslySelected,
  placeholder,
  onInputElement,
  onChange,
  onCountryIsoChanged,
  onBlur,
  onUpdatePreviouslySelected,
}: PhoneInputProps): JSX.Element => {
  const styles = usePhoneInputStyles({ isDisabled, hasError });

  /**
   * key is used to hard reload the IntlTelInput as it does not properly react on
   * preferredCountries/defaultCountry changes.
   */
  const [key, setKey] = useState(uuidV4());
  const hardReload = () => setKey(uuidV4());

  const inputRef = useRef<IntlTelInput>(null);
  const lastSelectedCountryRef = useRef<string | null>(null);
  const [defaultCountryToApply, setDefaultCountryToApply] =
    useState(defaultCountry);

  /**
   * If passed defaultCountry does not exists in the onlyCountries, set defaultCountry to DEFAULT_COUNTRY
   */
  useEffect(() => {
    if (defaultCountry) {
      const country = availableCountriesMap[defaultCountry]
        ? defaultCountry
        : DEFAULT_COUNTRY;
      setDefaultCountryToApply(country);
      hardReload();
    }
  }, [defaultCountry]);
  /**
   * Manages fullNumber change from the outside.
   * We would need also to hard reload component to make defaultValue be updated.
   */
  const fullNumberRef = useRef(fullNumber);
  useEffect(() => {
    if (fullNumberRef.current === fullNumber) {
      return;
    }

    hardReload();
    fullNumberRef.current = fullNumber;
  }, [fullNumber]);

  useEffect(() => {
    if (inputRef.current) {
      onInputElement?.(inputRef.current?.tel);
    }
  }, [key]);

  const updateCountryInfo = (iso2?: string) => {
    if (iso2 && lastSelectedCountryRef.current !== iso2) {
      lastSelectedCountryRef.current = iso2;
      onCountryIsoChanged?.(iso2);
      onUpdatePreviouslySelected?.(iso2);
    }
  };

  const updateValueFromFullNumber = (fullNumber: string): void => {
    /**
     * Remove spaces/dashed from the full number. "+34 111 111-111" -> "+34111111111"
     */
    const value = fullNumber.replace(/[\s-()]/g, '');
    fullNumberRef.current = value;
    onChange(value);
  };

  return (
    <Box
      css={styles}
      sx={{
        display: 'flex',
      }}
    >
      <IntlTelInput
        key={key}
        fieldId={id}
        fieldName={name}
        ref={inputRef}
        preferredCountries={previouslySelected}
        disabled={isDisabled}
        format
        defaultValue={fullNumber}
        /**
         * Added to fix issue when the country list does not close when you click
         * on the input that has a value.
         */
        containerClassName='intl-tel-input'
        inputClassName='phone-input'
        useMobileFullscreenDropdown={false}
        autoFocus={autoFocus}
        onlyCountries={availableCountries}
        placeholder={placeholder}
        defaultCountry={defaultCountryToApply}
        onPhoneNumberBlur={onBlur}
        onSelectFlag={(_, countryData, fullNumber) => {
          updateCountryInfo(countryData.iso2);
          updateValueFromFullNumber(fullNumber);
        }}
        onPhoneNumberChange={(_isValid, _value, countryData, fullNumber) => {
          updateCountryInfo(countryData.iso2);
          updateValueFromFullNumber(fullNumber);
        }}
      />
      {hasError && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 2,
          }}
        >
          <Status statusLevel='error' size={20} />
        </Box>
      )}
    </Box>
  );
};
